import React from "react";
import { useLoaderData } from "react-router-dom";
import { getAdminStats } from "../../../data/admin/stats";

export async function loader() {
    const stats = await getAdminStats();
    return { stats };
}

function formatDate(value) {
    return new Date(value).toLocaleDateString("fr-FR");
}

function formatDateTime(value) {
    return new Date(value).toLocaleString("fr-FR");
}

function formatOwnerLabel(owner) {
    const name = [owner.firstname, owner.lastname].filter(Boolean).join(" ").trim();
    if (name) {
        return name;
    }
    if (owner.email) {
        return owner.email;
    }
    return `Utilisateur ${owner.userId ?? ""}`.trim();
}

function getWeekLabel(range) {
    const start = new Date(range.start);
    const endExclusive = new Date(range.endExclusive);
    const endInclusive = new Date(endExclusive);
    endInclusive.setDate(endInclusive.getDate() - 1);
    return `Du ${start.toLocaleDateString("fr-FR")} au ${endInclusive.toLocaleDateString("fr-FR")}`;
}

function computeDelta(current, previous) {
    const delta = current - previous;
    const percent = previous === 0 ? null : (delta / previous) * 100;
    return { delta, percent };
}

function Delta({ current, previous }) {
    const { delta, percent } = computeDelta(current, previous);
    const deltaClass = delta > 0 ? "delta-positive" : delta < 0 ? "delta-negative" : "delta-neutral";
    const deltaSign = delta > 0 ? "+" : "";
    const percentLabel = percent === null ? "n/a" : `${deltaSign}${percent.toFixed(1)}%`;

    return (
        <div className={`admin-stats-delta ${deltaClass}`}>
            <span>{`${deltaSign}${delta}`}</span>
            <span className="admin-stats-delta-percent">({percentLabel})</span>
        </div>
    );
}

function StatCard({ title, total, thisWeek, lastWeek, children }) {
    return (
        <section className="admin-stats-card">
            <header className="admin-stats-card-header">
                <h2>{title}</h2>
                <div className="admin-stats-total">{total.toLocaleString("fr-FR")}</div>
                <div className="admin-stats-total-label">Total</div>
            </header>
            <div className="admin-stats-weeks">
                <div className="admin-stats-week-block">
                    <div className="admin-stats-week-label">Cette semaine</div>
                    <div className="admin-stats-week-value">{thisWeek.toLocaleString("fr-FR")}</div>
                </div>
                <div className="admin-stats-week-block">
                    <div className="admin-stats-week-label">Semaine dernière</div>
                    <div className="admin-stats-week-value">{lastWeek.toLocaleString("fr-FR")}</div>
                </div>
            </div>
            <Delta current={thisWeek} previous={lastWeek} />
            {children}
        </section>
    );
}

export default function AdminStatsPage() {
    const { stats } = useLoaderData();

    const currentWeekLabel = getWeekLabel(stats.currentWeek);
    const previousWeekLabel = getWeekLabel(stats.previousWeek);
    const topOwners = stats.topOwners ?? [];

    return (
        <div className="admin-stats-page">
            <div className="admin-stats-header">
                <h1>Statistiques d&apos;administration</h1>
                <p className="admin-stats-generated">Mise à jour : {formatDateTime(stats.generatedAt)}</p>
                <div className="admin-stats-week-range">
                    <span>{currentWeekLabel}</span>
                    <span className="admin-stats-week-separator">|</span>
                    <span>{previousWeekLabel}</span>
                </div>
            </div>

            <div className="admin-stats-grid">
                <StatCard
                    title="Utilisateurs"
                    total={stats.users.total}
                    thisWeek={stats.users.thisWeek}
                    lastWeek={stats.users.lastWeek}
                >
                    <div className="admin-stats-extra">
                        <div className="admin-stats-extra-label">Sans annonce</div>
                        <div className="admin-stats-extra-value">{stats.owners.usersWithoutListings.toLocaleString("fr-FR")}</div>
                    </div>
                    <div className="admin-stats-toplist">
                        <div className="admin-stats-toplist-title">Top 5 utilisateurs (annonces)</div>
                        {topOwners.length === 0 && <i>Aucune donnée.</i>}
                        {topOwners.length > 0 && (
                            <ol className="admin-stats-toplist-items">
                                {topOwners.map((owner) => (
                                    <li key={owner.userId} className="admin-stats-toplist-item">
                                        <a className="admin-stats-toplist-link" href={`/user/${owner.userId}`}>
                                            <span className="admin-stats-toplist-name">{formatOwnerLabel(owner)}</span>
                                        </a>
                                        <span className="admin-stats-toplist-count">
                                            {owner.listingsCount.toLocaleString("fr-FR")} annonces
                                        </span>
                                    </li>
                                ))}
                            </ol>
                        )}
                    </div>
                </StatCard>

                <StatCard
                    title="Annonces"
                    total={stats.listings.total}
                    thisWeek={stats.listings.thisWeek}
                    lastWeek={stats.listings.lastWeek}
                >
                    <div className="admin-stats-split">
                        <div className="admin-stats-split-item">
                            <span className="admin-stats-split-label">Actives</span>
                            <span className="admin-stats-split-value">{stats.listings.active.toLocaleString("fr-FR")}</span>
                        </div>
                        <div className="admin-stats-split-item">
                            <span className="admin-stats-split-label">En validation</span>
                            <span className="admin-stats-split-value">{stats.listings.pendingValidation.toLocaleString("fr-FR")}</span>
                        </div>
                    </div>
                </StatCard>

                <StatCard
                    title="Locations"
                    total={stats.rentals.total}
                    thisWeek={stats.rentals.thisWeek}
                    lastWeek={stats.rentals.lastWeek}
                >
                    <div className="admin-stats-rentals">
                        <div className="admin-stats-rental-item">
                            <span>Brouillons</span>
                            <strong>{stats.rentals.draft.toLocaleString("fr-FR")}</strong>
                        </div>
                        <div className="admin-stats-rental-item">
                            <span>À venir</span>
                            <strong>{stats.rentals.upcoming.toLocaleString("fr-FR")}</strong>
                        </div>
                        <div className="admin-stats-rental-item">
                            <span>En cours</span>
                            <strong>{stats.rentals.inProgress.toLocaleString("fr-FR")}</strong>
                        </div>
                        <div className="admin-stats-rental-item">
                            <span>Terminées</span>
                            <strong>{stats.rentals.completed.toLocaleString("fr-FR")}</strong>
                        </div>
                        <div className="admin-stats-rental-item">
                            <span>Annulées</span>
                            <strong>{stats.rentals.cancelled.toLocaleString("fr-FR")}</strong>
                        </div>
                    </div>
                </StatCard>

                <section className="admin-stats-card admin-stats-card-engagement">
                    <header className="admin-stats-card-header">
                        <h2>Engagement loueurs</h2>
                        <div className="admin-stats-total">{stats.owners.ownersWithListings.toLocaleString("fr-FR")}</div>
                        <div className="admin-stats-total-label">Avec au moins une annonce</div>
                    </header>
                    <div className="admin-stats-engagement-grid">
                        <div className="admin-stats-engagement-item">
                            <span className="admin-stats-engagement-label">Multi-annonces</span>
                            <strong className="admin-stats-engagement-value">{stats.owners.ownersWithMultipleListings.toLocaleString("fr-FR")}</strong>
                        </div>
                        <div className="admin-stats-engagement-item">
                            <span className="admin-stats-engagement-label">Moyenne / loueur</span>
                            <strong className="admin-stats-engagement-value">{stats.owners.averageListingsPerOwner.toLocaleString("fr-FR")}</strong>
                        </div>
                        <div className="admin-stats-engagement-item">
                            <span className="admin-stats-engagement-label">Utilisateurs sans annonce</span>
                            <strong className="admin-stats-engagement-value">{stats.owners.usersWithoutListings.toLocaleString("fr-FR")}</strong>
                        </div>
                    </div>
                    <div className="admin-stats-footnote">
                        Calculé sur les annonces non supprimées. Périodes : {currentWeekLabel.toLowerCase()} et {previousWeekLabel.toLowerCase()}.
                    </div>
                    <div className="admin-stats-meta">
                        <span>Début semaine courante : {formatDate(stats.currentWeek.start)}</span>
                        <span>Début semaine précédente : {formatDate(stats.previousWeek.start)}</span>
                    </div>
                </section>
            </div>
        </div>
    );
}
